import React from 'react';
import { useTranslation } from 'react-i18next';
import { Appicon_blue } from '../../../Components/Icons/brand/Appicon_blue';
import { Onefit } from '../../../Components/Icons/3rdParty/Onefit';
import { USC } from '../../../Components/Icons/3rdParty/USC';
import {
  Button,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
  FormHelperText,
} from '../../../UIKit';
import { Field, FormikErrors, FormikTouched } from 'formik';
import { IOnboardingData } from '../IOnboardingData.model';
import { isValueDefined } from '../../../Helpers/CleanData.helper';
import { ICityCountry } from '~/Models/DTO/CoreProvider.model';
import { selectClasses } from '@mui/material';
import { makeStyles } from '~/UIKit/Style';
import { LocationEditCustomInputAddress } from '~/Screens/Settings/ProviderLocations/LocationEdit/LocationEditSections/LocationEditCustomElements';
interface IOnboardingStudioProps {
  studioTypes: number[];
  countryList: ICityCountry[];
  isStudioSetupDone: boolean;
  onDoneSettingUpStudio: () => void;
  errors: FormikErrors<IOnboardingData>;
  touched: FormikTouched<IOnboardingData>;
  postLogin?: boolean;
}

const useStyles = makeStyles((theme) => ({
  onboardingStudioContainer: {
    width: 400,
    [theme.breakpoints.down('md')]: {
      width: 300,
    },
    margin: 'auto',
  },

  onboardingStudioTitle: {
    fontSize: '28px',
    fontWeight: 'bold',
    lineHeight: '32px',
    textAlign: 'center',
    overflowWrap: 'break-word',
  },

  onboardingStudioLabel: {
    fontWeight: 'bold',
    lineHeight: '27px',
    fontSize: '14px',
    marginTop: 30,
    [theme.breakpoints.down('md')]: {
      marginTop: 20,
    },
  },

  onboardingStudioFormControl: {
    margin: '8px 0',
  },

  onboardingStudioInput: {
    width: 400,
    [theme.breakpoints.down('md')]: {
      width: 300,
    },
    [`& .${selectClasses['select']}`]: {
      display: 'flex',
      alignItems: 'center',
    },
  },

  onboardingStudioTypeIcon: {
    margin: '2px 8px',
  },

  onboardingStudioConfirmButton: {
    marginTop: 30,
    width: 400,
    height: 50,
    fontSize: '1rem',
    borderRadius: 4,
    [theme.breakpoints.down('md')]: {
      width: 300,
    },
  },
}));

export const OnboardingStudio = ({
  studioTypes,
  countryList,
  isStudioSetupDone,
  onDoneSettingUpStudio,
  errors,
  touched,
  postLogin = false,
}: IOnboardingStudioProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const countrySelections = postLogin
    ? countryList.map((country: ICityCountry) => ({
        label: country?.nameEnglish || '',
        value: country?.codeA2 || '',
        selected: false,
      }))
    : [];
  return (
    <div className={classes.onboardingStudioContainer}>
      <Typography className={classes.onboardingStudioTitle}>
        {t('onboarding.studio.join')}
      </Typography>
      <Typography className={classes.onboardingStudioLabel}>
        {t('onboarding.studio.question.studioName')}
      </Typography>
      <FormControl error={touched.studioName && isValueDefined(errors.studioName)}>
        <Field
          className={classes.onboardingStudioInput}
          name="studioName"
          label={t('onboarding.studio.placeholder.studioName')}
          component={TextField}
          fullWidth
          placeholder={t('onboarding.studio.placeholder.studioName')}
          error={touched.studioName && isValueDefined(errors.studioName)}
          inputProps={{ maxLength: 50 }}
        />
        <FormHelperText>{touched.studioName && errors.studioName}</FormHelperText>
      </FormControl>
      <Typography className={classes.onboardingStudioLabel}>
        {t('onboarding.studio.question.kindOfStudio')}
      </Typography>
      <FormControl variant="outlined" className={classes.onboardingStudioFormControl}>
        <Field
          className={classes.onboardingStudioInput}
          labelId="onboarding-studioType-select"
          name="studioType"
          component={Select}
          fullWidth
          placeholder={t('pro.select')}
          helperText={t('onboarding.studio.question.message.studioName')}
        >
          {studioTypes.map((type) => (
            <MenuItem
              value={type}
              key={`search.categories.${type}`}
              data-testid={`onboarding.studioType-${type}`}
            >
              {t(`search.categories.${type}`)}
            </MenuItem>
          ))}
        </Field>
      </FormControl>
      <Typography className={classes.onboardingStudioLabel}>
        {t('onboarding.personalDetails.question.studioLocation')}
      </Typography>
      {!postLogin && (
        <FormControl variant="outlined" className={classes.onboardingStudioFormControl}>
          <Field
            className={classes.onboardingStudioInput}
            labelId="onboarding-countryName-select"
            name="countryName"
            component={Select}
            fullWidth
          >
            {countryList.map((country) => (
              <MenuItem value={country?.nameEnglish} key={`country.name.${country?.nameEnglish}`}>
                {t(`country.name.${country?.nameEnglish?.toLowerCase()}`)}
              </MenuItem>
            ))}
          </Field>
        </FormControl>
      )}
      {postLogin && (
        <FormControl
          error={touched.locationName && isValueDefined(errors.locationName)}
          className={classes.onboardingStudioFormControl}
        >
          <Field
            className={classes.onboardingStudioInput}
            name="locationName"
            label={t('onboarding.studio.placeholder.locationName')}
            component={TextField}
            fullWidth
            placeholder={t('onboarding.studio.placeholder.locationName')}
            error={touched.locationName && isValueDefined(errors.locationName)}
            inputProps={{ maxLength: 50 }}
          />
          <FormHelperText>{touched.locationName && errors.locationName}</FormHelperText>
        </FormControl>
      )}
      {postLogin && <Field hidden name="latitude" />}
      {postLogin && <Field hidden name="longitude" />}
      {postLogin && <LocationEditCustomInputAddress countries={countrySelections} simple />}
      {postLogin && (
        <FormControl
          error={touched.countryName && isValueDefined(errors.countryName)}
          className={classes.onboardingStudioFormControl}
        >
          <Field
            className={classes.onboardingStudioInput}
            name="countryName"
            label={t('form.field.location.country')}
            component={TextField}
            fullWidth
            placeholder={t('form.field.location.country')}
            error={touched.countryName && isValueDefined(errors.countryName)}
            inputProps={{ maxLength: 50 }}
            InputLabelProps={{
              shrink: touched.countryName || errors.countryName,
            }}
            disabled
          />
          <FormHelperText>{touched.countryName && errors.countryName}</FormHelperText>
        </FormControl>
      )}
      {postLogin && (
        <FormControl
          error={touched.city && isValueDefined(errors.city)}
          className={classes.onboardingStudioFormControl}
        >
          <Field
            className={classes.onboardingStudioInput}
            name="city"
            label={t('form.field.location.city')}
            component={TextField}
            fullWidth
            placeholder={t('form.field.location.city')}
            error={touched.city && isValueDefined(errors.city)}
            inputProps={{ maxLength: 50 }}
            InputLabelProps={{
              shrink: touched.city || errors.city,
            }}
          />
          <FormHelperText>{touched.city && errors.city}</FormHelperText>
        </FormControl>
      )}
      {postLogin && (
        <FormControl
          error={touched.street && isValueDefined(errors.street)}
          className={classes.onboardingStudioFormControl}
        >
          <Field
            className={classes.onboardingStudioInput}
            name="street"
            label={t('form.field.location.street')}
            component={TextField}
            fullWidth
            placeholder={t('form.field.location.street')}
            error={touched.street && isValueDefined(errors.street)}
            inputProps={{ maxLength: 50 }}
            InputLabelProps={{
              shrink: touched.street || errors.street,
            }}
          />
          <FormHelperText>{touched.street && errors.street}</FormHelperText>
        </FormControl>
      )}
      {postLogin && (
        <FormControl
          error={touched.zipCode && isValueDefined(errors.zipCode)}
          className={classes.onboardingStudioFormControl}
        >
          <Field
            className={classes.onboardingStudioInput}
            name="zipCode"
            label={t('form.field.location.zip')}
            component={TextField}
            fullWidth
            placeholder={t('form.field.location.zip')}
            error={touched.zipCode && isValueDefined(errors.zipCode)}
            inputProps={{ maxLength: 50 }}
            InputLabelProps={{
              shrink: touched.zipCode || errors.zipCode,
            }}
          />
          <FormHelperText>{touched.zipCode && errors.zipCode}</FormHelperText>
        </FormControl>
      )}
      {!postLogin ? (
        <FormControl
          error={touched.aggregatorType && isValueDefined(errors.aggregatorType)}
          className={classes.onboardingStudioFormControl}
        >
          <Field
            className={classes.onboardingStudioInput}
            labelId="onboarding-aggregatorType-select"
            name="aggregatorType"
            label={t('onboarding.studio.question.typeOfAggregator')}
            component={Select}
            fullWidth
            placeholder={t('onboarding.studio.question.typeOfAggregator')}
          >
            <MenuItem value={0} data-testid="onboarding.studioAggregator-none">
              <Appicon_blue className={classes.onboardingStudioTypeIcon} />
              {t('integrations.none.title')}
            </MenuItem>
            <MenuItem value={1} data-testid="onboarding.studioAggregator-usc">
              <USC className={classes.onboardingStudioTypeIcon} />
              {t('integrations.usc.title')}
            </MenuItem>
            <MenuItem value={2} data-testid="onboarding.studioAggregator-onefit">
              <Onefit className={classes.onboardingStudioTypeIcon} />
              {t('integrations.onefit.title')}
            </MenuItem>
          </Field>
        </FormControl>
      ) : (
        <input type="hidden" name="aggregatorType" />
      )}
      <Button
        data-testid="onboarding.studioConfirmButton"
        className={classes.onboardingStudioConfirmButton}
        disabled={!isStudioSetupDone}
        onClick={onDoneSettingUpStudio}
        variant="contained"
      >
        {t('app.continue')}
      </Button>
    </div>
  );
};
